import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import { useApi } from "@/hooks/useApi";
import type { MemberDetail as MemberDetailType } from "@/lib/types";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import Avatar from "@/components/base/Avatar";
import Lightbox from "@/components/base/Lightbox";
import ContactModal from "@/components/feature/ContactModal";
import { careerPillClass } from "@/components/feature/MemberCard";
import { contrastText, validColor } from "@/lib/color";
import MiniMap from "../components/MiniMap";
import { mediaUrl } from "@/lib/media";

const FALLBACK_COLOR = "#17408B";

export default function MemberDetail() {
  const { no } = useParams<{ no: string }>();
  const { data, loading, error, reload } = useApi<MemberDetailType | MemberDetailType[]>(
    no ? `member_view.php?no=${no}` : null
  );
  const [contactOpen, setContactOpen] = useState(false);
  const [lightbox, setLightbox] = useState<number | null>(null);

  const member: MemberDetailType | null = Array.isArray(data) ? data[0] ?? null : data;

  const cardImages = useMemo(() => {
    if (!member) return [];
    return [member.card_img1, member.card_img2].filter(Boolean) as string[];
  }, [member]);

  const cate = String(member?.cate_name || member?.cate || member?.category || "").trim();
  const color = validColor(String(member?.color ?? "")) || FALLBACK_COLOR;
  const lat = Number(member?.lat);
  const lon = Number(member?.lon);

  return (
    <>
      <PageBanner
        title="회원 상세"
        breadcrumb={[{ label: "회원", to: "/members" }, { label: "상세" }]}
      />

      <section className="mx-auto max-w-4xl px-4 md:px-6 py-10 md:py-12">
        {loading ? (
          <div className="space-y-6">
            <div className="flex items-center gap-5">
              <Skeleton className="h-24 w-24 rounded-full" />
              <div className="space-y-3">
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
            <Skeleton className="h-32 w-full rounded-xl" />
            <Skeleton className="h-48 w-full rounded-xl" />
          </div>
        ) : error ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="회원 정보를 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        ) : !member ? (
          <EmptyState
            icon="ri-user-line"
            title="존재하지 않는 회원입니다"
            action={
              <Link
                to="/members"
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                목록으로
              </Link>
            }
          />
        ) : (
          <div className="space-y-6">
            {/* 프로필 */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 rounded-xl border border-background-200 bg-background-50 p-6">
              <Avatar name={member.name} photo={member.photo} color={color} size={96} />
              <div className="text-center sm:text-left">
                <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  {member.name}
                  {member.aho && (
                    <span className="ml-2 text-base font-medium text-foreground-500">
                      ({member.aho})
                    </span>
                  )}
                </h1>
                {member.position && (
                  <p className="mt-1.5 text-sm font-medium text-foreground-700">
                    {member.position}
                  </p>
                )}
                {cate && (
                  <span
                    className="mt-3 inline-block rounded-full px-3 py-1 text-xs font-semibold"
                    style={{ backgroundColor: color, color: contrastText(color) }}
                  >
                    {cate}
                  </span>
                )}
              </div>
            </div>

            {/* 로타리 경력 */}
            {member.career && member.career.length > 0 && (
              <div className="rounded-xl border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  로타리 경력
                </h2>
                <div className="mt-4 flex flex-col items-start gap-1.5">
                  {member.career.map((c, i) => (
                    <span
                      key={i}
                      className={`inline-block max-w-full whitespace-normal break-keep rounded-md px-2.5 py-1 text-[12.5px] leading-[1.35] ${careerPillClass(c)}`}
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* 사업장 정보 (biz_name 비어 있으면 전체 숨김) */}
            {member.biz_name && (
              <div className="rounded-xl border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  사업장 정보
                </h2>
                <dl className="mt-4 space-y-3 text-sm">
                  <div className="flex gap-3">
                    <dt className="w-16 shrink-0 text-foreground-500">직장명</dt>
                    <dd className="font-medium text-foreground-900">{member.biz_name}</dd>
                  </div>
                  {member.position && (
                    <div className="flex gap-3">
                      <dt className="w-16 shrink-0 text-foreground-500">직책</dt>
                      <dd className="text-foreground-800">{member.position}</dd>
                    </div>
                  )}
                  {member.address && (
                    <div className="flex gap-3">
                      <dt className="w-16 shrink-0 text-foreground-500">주소</dt>
                      <dd className="text-foreground-800">{member.address}</dd>
                    </div>
                  )}
                  {member.tel && (
                    <div className="flex gap-3">
                      <dt className="w-16 shrink-0 text-foreground-500">전화</dt>
                      <dd>
                        <a
                          href={`tel:${String(member.tel).replace(/[^0-9+]/g, "")}`}
                          className="text-primary-700 hover:underline"
                        >
                          {member.tel}
                        </a>
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            {/* 지도 + 액션 */}
            {member.has_map === 1 && Number.isFinite(lat) && Number.isFinite(lon) && (
              <div className="rounded-xl border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">
                  위치
                </h2>
                <MiniMap lat={lat} lon={lon} name={member.biz_name || member.name} className="h-64 w-full" />
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {member.tel && (
                    <a
                      href={`tel:${String(member.tel).replace(/[^0-9+]/g, "")}`}
                      className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100 transition-colors"
                    >
                      <i className="ri-phone-line" aria-hidden="true"></i>
                      전화걸기
                    </a>
                  )}
                  <a
                    href={`https://map.kakao.com/link/to/${encodeURIComponent(member.biz_name || member.name)},${lat},${lon}`}
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100 transition-colors"
                  >
                    <i className="ri-navigation-line" aria-hidden="true"></i>
                    길찾기
                  </a>
                  <button
                    type="button"
                    onClick={() => setContactOpen(true)}
                    className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
                  >
                    <i className="ri-mail-send-line" aria-hidden="true"></i>
                    문의하기
                  </button>
                </div>
              </div>
            )}

            {/* 명함 이미지 */}
            {cardImages.length > 0 && (
              <div className="rounded-xl border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">
                  명함
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {cardImages.map((img, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setLightbox(i)}
                      className="overflow-hidden rounded-lg border border-background-200 bg-background-100"
                      aria-label={`명함 ${i + 1} 크게 보기`}
                    >
                      <span className="relative block aspect-[9/5] w-full">
                        <img
                          src={mediaUrl(img)}
                          alt={`${member.name} 명함 ${i + 1}`}
                          className="h-full w-full object-contain"
                        />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-center">
              <Link
                to="/members"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-200 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100"
              >
                <i className="ri-list-check" aria-hidden="true"></i>
                목록으로
              </Link>
            </div>
          </div>
        )}
      </section>

      <ContactModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        memNo={member?.no}
        title={member?.biz_name ? `${member.biz_name} 문의하기` : "문의하기"}
      />

      {lightbox != null && (
        <Lightbox
          images={cardImages}
          index={lightbox}
          onIndexChange={setLightbox}
          onClose={() => setLightbox(null)}
          alt={`${member?.name ?? ""} 명함`}
        />
      )}
    </>
  );
}